import {
  acceptSupportInvitation,
  previewSupportInvitation,
} from 'api/services/accessControlOperationsService'
import useAuthService from 'api/services/authService'
import useFavoritesService from 'api/services/favoritesService'
import {
  clearPendingApplyListingId,
  getPendingApplyListingId,
  setPendingApplyListingId,
} from 'api/utils/pendingApplyListing'
import { markListingOwnerIntent } from 'api/utils/pendingCreateAdFlow'
import { validateEmail } from 'api/utils/validateEmail'
import AuthPageShell from 'components/auth/AuthPageShell'
import { EmailInput } from 'components/common/field'
import PasswordInput from 'components/common/field/Password'
import UnderLink from 'components/common/UnderLink'
import { WizardStepChangeEventHandler } from 'components/types'
import { Location } from 'newTypes/location'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FieldChangeEventAnswer } from 'types/general'

import { i18n } from '../i18n'
import { getBackgroundPathFromState } from '../utils/authNavigation'
import { getFriendlyLoginErrorMessage } from '../utils/loginErrorMessages'

const GoogleIcon = () => (
  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  </span>
)

const FacebookIcon = () => (
  <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[#1877F2]">
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.49 0-1.953.93-1.953 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
    </svg>
  </span>
)

const AuthUserPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const inviteToken = useMemo(
    () => (new URLSearchParams(location.search).get('invite') || '').trim(),
    [location.search]
  )
  const { refresh: refreshPermissions } = usePermissions()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const mode = params.get('mode')
    const oobCode = params.get('oobCode')
    if (oobCode && (mode === 'resetPassword' || !mode)) {
      navigate(`/reset-password-action${location.search}`, { replace: true })
    }
  }, [location.search, location.pathname, navigate])

  const dismissAuth = () => {
    const pending =
      (location.state as { applyListingId?: string } | null)?.applyListingId ||
      getPendingApplyListingId()
    clearPendingApplyListingId()
    if (pending) {
      navigate(`/listing/${pending}`)
      return
    }
    navigate(getBackgroundPathFromState(location.state))
  }

  const { login, loginWithGoogle, loginWithFacebook, isAuthenticated } =
    useAuthService()

  const claimSupportInvite = async () => {
    if (!inviteToken) return false
    try {
      await acceptSupportInvitation(inviteToken)
      await refreshPermissions()
      return true
    } catch {
      await refreshPermissions()
      return false
    }
  }

  const labelEmail = i18n.translate('common.email')
  const labelPassword = i18n.translate('common.password')
  const labelForgotPassword = i18n.translate('common.forgotPassword')
  const buttonLogin = i18n.translate('auth.logIn')
  const loginSubtitle = i18n.translate('auth.loginSubtitle')
  const orLabel = i18n.translate('auth.orContinueWith')
  const noAccount = i18n.translate('auth.noAccount')
  const signUpLabel = i18n.translate('auth.signUp')

  const { addFavorite } = useFavoritesService()
  const [loading, setLoading] = useState(false)

  const [userCredentials, setUserCredentials] = useState({
    email: '',
    password: '',
  })

  const [fieldValidity, setFieldValidity] = useState({
    email: false,
    password: false,
  })
  const formRef = useRef<HTMLFormElement | null>(null)

  const { mutateAsync: favoriteMutation } = useMutation(addFavorite)

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement>
      | WizardStepChangeEventHandler
      | FieldChangeEventAnswer
      | Location
  ) => {
    if ('target' in e) {
      const { name, value } = e.target
      setUserCredentials(prev => ({
        ...prev,
        [name]: value,
      }))
    } else if ('name' in e && 'value' in e) {
      if (typeof e.value === 'string') {
        setUserCredentials(prev => ({
          ...prev,
          [e.name]: e.value,
        }))
      }
    }
  }

  const handleFieldValidation = (name: string, isValid: boolean) => {
    setFieldValidity(prev => ({
      ...prev,
      [name]: isValid,
    }))
  }

  const syncCredentialsFromForm = () => {
    const form = formRef.current
    if (!form) return
    const emailInput = form.elements.namedItem('email') as HTMLInputElement | null
    const passwordInput = form.elements.namedItem(
      'password'
    ) as HTMLInputElement | null
    const email = emailInput?.value ?? userCredentials.email
    const password = passwordInput?.value ?? userCredentials.password
    if (email !== userCredentials.email || password !== userCredentials.password) {
      setUserCredentials(prev => ({ ...prev, email, password }))
    }
  }

  const isFormValid =
    (fieldValidity.email && fieldValidity.password) ||
    (validateEmail(userCredentials.email) && userCredentials.password.length > 0)

  const redirectAfterSuccessfulLogin = async () => {
    const becameSupport = await claimSupportInvite()
    if (becameSupport || inviteToken) {
      toast.success(String(i18n.translate('auth.supportInviteAccepted')))
      navigate('/support-console', { replace: true })
      return
    }

    const resumeCreateAd = Boolean(
      (location.state as { resumeCreateAd?: boolean } | null)?.resumeCreateAd
    )
    if (resumeCreateAd) {
      markListingOwnerIntent()
      await refreshPermissions({ intent: 'list_property' })
      // Draft restores the form; do not pass resumeCreateAd (causes false "expired").
      navigate('/create-ad', { replace: true })
      return
    }

    const fromState = (location.state as { applyListingId?: string } | null)
      ?.applyListingId
    const fromStorage = getPendingApplyListingId()
    const applyListingId = fromState || fromStorage || undefined
    if (applyListingId) {
      clearPendingApplyListingId()
      navigate(`/listing/${applyListingId}`, {
        state: { resumeInterest: true },
        replace: true,
      })
      return
    }

    const returnToFromQuery =
      new URLSearchParams(location.search).get('returnTo') || undefined
    const fromRoute = (
      location.state as {
        from?: { pathname?: string; search?: string; hash?: string }
      } | null
    )?.from
    const backgroundRoute = (
      location.state as {
        backgroundLocation?: {
          pathname?: string
          search?: string
          hash?: string
        }
      } | null
    )?.backgroundLocation
    const returnTo =
      returnToFromQuery ||
      (fromRoute?.pathname
        ? `${fromRoute.pathname}${fromRoute.search || ''}${fromRoute.hash || ''}`
        : undefined) ||
      (backgroundRoute?.pathname
        ? `${backgroundRoute.pathname}${backgroundRoute.search || ''}${backgroundRoute.hash || ''}`
        : undefined)

    if (location.state?.favorite) {
      await favoriteMutation(location.state.favorite)
    }
    navigate(returnTo || '/account', { replace: true })
  }

  const handleLogin = async () => {
    syncCredentialsFromForm()
    const email =
      (formRef.current?.elements.namedItem('email') as HTMLInputElement | null)
        ?.value ?? userCredentials.email
    const password =
      (
        formRef.current?.elements.namedItem(
          'password'
        ) as HTMLInputElement | null
      )?.value ?? userCredentials.password
    if (!validateEmail(email) || password.length === 0) return

    setLoading(true)
    try {
      // Mark before Firebase login so the first /me-permissions bootstraps owner.
      const asOwner = Boolean(
        (location.state as { resumeCreateAd?: boolean } | null)?.resumeCreateAd
      )
      if (asOwner) markListingOwnerIntent()
      await login(email, password, asOwner ? { asOwner: true } : undefined)
      await redirectAfterSuccessfulLogin()
    } catch (err: unknown) {
      toast.error(getFriendlyLoginErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (loading) return
    await handleLogin()
  }

  const handleSocial = async (provider: 'google' | 'facebook') => {
    setLoading(true)
    try {
      const asOwner = Boolean(
        (location.state as { resumeCreateAd?: boolean } | null)?.resumeCreateAd
      )
      if (asOwner) markListingOwnerIntent()
      const opts = asOwner
        ? { asOwner: true as const, allowRedirect: false as const }
        : undefined
      if (provider === 'google') await loginWithGoogle(opts)
      else await loginWithFacebook(opts)
      await redirectAfterSuccessfulLogin()
    } catch (err: unknown) {
      toast.error(getFriendlyLoginErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const applyId = (location.state as { applyListingId?: string } | null)
      ?.applyListingId
    if (applyId) {
      setPendingApplyListingId(applyId)
    }
  }, [(location.state as { applyListingId?: string } | null)?.applyListingId])

  useEffect(() => {
    const emailFromQuery = new URLSearchParams(location.search).get('email')
    const emailFromState = location.state?.email
    const emailCandidate =
      (typeof emailFromQuery === 'string' && emailFromQuery.trim()) ||
      (typeof emailFromState === 'string' && emailFromState.trim()) ||
      ''
    if (!emailCandidate) return
    setUserCredentials(prev => ({ ...prev, email: emailCandidate }))
    if (validateEmail(emailCandidate)) {
      setFieldValidity(prev => ({ ...prev, email: true }))
    }
  }, [location.search, location.state?.email])

  useEffect(() => {
    if (!inviteToken) return
    let cancelled = false
    ;(async () => {
      try {
        const preview = await previewSupportInvitation(inviteToken)
        if (cancelled) return
        const email = preview.email.trim().toLowerCase()
        setUserCredentials(prev => ({ ...prev, email }))
        setFieldValidity(prev => ({
          ...prev,
          email: validateEmail(email) === true,
        }))
      } catch {
        // keep form usable; accept will fail later if invite is bad
      }
    })()
    return () => {
      cancelled = true
    }
  }, [inviteToken])

  useEffect(() => {
    if (!isAuthenticated || location.pathname !== '/auth') return

    // create-ad login/register is handled by redirectAfterSuccessfulLogin — do not
    // steal navigation to /account as tenant before the listing is published.
    const resumeCreateAd = Boolean(
      (location.state as { resumeCreateAd?: boolean } | null)?.resumeCreateAd
    )
    if (resumeCreateAd) return

    let cancelled = false
    const resume = async () => {
      const pending = getPendingApplyListingId()
      if (pending) {
        clearPendingApplyListingId()
        if (!cancelled) {
          navigate(`/listing/${pending}`, {
            state: { resumeInterest: true },
            replace: true,
          })
        }
        return
      }

      const returnToFromQuery =
        new URLSearchParams(location.search).get('returnTo') || undefined
      const fromRoute = (
        location.state as {
          from?: { pathname?: string; search?: string; hash?: string }
        } | null
      )?.from
      const backgroundRoute = (
        location.state as {
          backgroundLocation?: {
            pathname?: string
            search?: string
            hash?: string
          }
        } | null
      )?.backgroundLocation
      const returnTo =
        returnToFromQuery ||
        (fromRoute?.pathname
          ? `${fromRoute.pathname}${fromRoute.search || ''}${fromRoute.hash || ''}`
          : undefined) ||
        (backgroundRoute?.pathname
          ? `${backgroundRoute.pathname}${backgroundRoute.search || ''}${backgroundRoute.hash || ''}`
          : undefined)

      const pendingFavorite = (location.state as { favorite?: string } | null)
        ?.favorite
      if (pendingFavorite) {
        try {
          await favoriteMutation(pendingFavorite)
        } catch {
          // continue
        }
      }

      if (!cancelled) {
        navigate(returnTo || '/account', { replace: true })
      }
    }

    void resume()
    return () => {
      cancelled = true
    }
  }, [
    isAuthenticated,
    location.pathname,
    location.state,
    navigate,
    favoriteMutation,
  ])

  return (
    <AuthPageShell
      title={i18n.translate('common.logIn')}
      subtitle={loginSubtitle}
    >
      <form
        ref={formRef}
        onSubmit={e => {
          void handleSubmit(e)
        }}
        className="flex flex-col gap-4 [&>div]:!my-0"
      >
        <EmailInput
          name="email"
          label={labelEmail}
          value={userCredentials.email}
          onChange={handleChange}
          setIsValid={isValid => handleFieldValidation('email', isValid)}
          isFirstInput
        />
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-imovSecondary">
              {labelPassword}
            </span>
            <UnderLink
              label={labelForgotPassword}
              redirectTo="/reset-password"
              isUnderline
              className="text-sm font-medium text-imovPrimary hover:text-imovPrimaryDark"
            />
          </div>
          <PasswordInput
            name="password"
            label=""
            value={userCredentials.password}
            onChange={handleChange}
            setIsValid={isValid => handleFieldValidation('password', isValid)}
            isValidationRequired={false}
          />
        </div>

        <button
          type="submit"
          disabled={!isFormValid || loading}
          className="mt-1 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-imovPrimary py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark disabled:cursor-not-allowed disabled:bg-imovPrimary/40"
        >
          {loading ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            <>
              {buttonLogin}
              <span aria-hidden>→</span>
            </>
          )}
        </button>
      </form>

      <div className="my-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-xs text-gray-400">{orLabel}</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          disabled={loading}
          onClick={() => void handleSocial('google')}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50 disabled:opacity-50"
        >
          <GoogleIcon />
          Google
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={() => void handleSocial('facebook')}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50 disabled:opacity-50"
        >
          <FacebookIcon />
          Facebook
        </button>
      </div>

      <p className="mt-6 text-center text-sm text-gray-600">
        <span>{noAccount} </span>
        <UnderLink
          label={signUpLabel}
          redirectTo={
            inviteToken
              ? `/create-user?invite=${encodeURIComponent(inviteToken)}`
              : '/create-user'
          }
          state={{
            from: (location.state as { from?: unknown } | null)?.from,
            applyListingId: (
              location.state as { applyListingId?: string } | null
            )?.applyListingId,
            resumeCreateAd: (
              location.state as { resumeCreateAd?: boolean } | null
            )?.resumeCreateAd,
          }}
          isUnderline
          className="inline font-semibold text-imovPrimary hover:text-imovPrimaryDark"
        />
      </p>

      <button
        type="button"
        onClick={dismissAuth}
        className="mt-3 w-full py-2 text-center text-sm text-gray-500 transition hover:text-imovSecondary"
      >
        {i18n.translate('common.cancel')}
      </button>
    </AuthPageShell>
  )
}

export default AuthUserPage
